import React, { useCallback, useEffect, useState } from "react";
import {
  CreateDomainStyles,
  NoSkillsText,
  OptionBtn,
  OptionsList
} from "./OptionsEditor.styled.js";
import { PlusCircle } from "@untitledui/icons/PlusCircle";
import { ActionButton, Modal } from "@talentbait/shared-components";
import {
  mapRequirementToChatbotQuestion,
  toggleListOption,
  updateProfileArrays
} from "../../../Profile.utils.js";
import Actions from "../../../../../Actions.js";
import logger from "../../../../../utils/LoggerFrontend.js";
import { useTranslation } from "react-i18next";
import CreateDomainSkillForm from "../../../modals/CreateDomainSkill.jsx";
import LoadingIndicator from "react-loading-indicator";
import { withRouter } from "react-router-dom";
import withRefluxStore from "../../../../../Stores/withRefluxStore.jsx";
import ProfilesStore from "../../../Profile.store.js";

const loadingColor = {
  red: 255,
  green: 255,
  blue: 255,
  alpha: 1
};

const ListOption = ({
  index,
  req,
  filteredSkills,
  // Store
  profile
}) => {
  const { t } = useTranslation();
  const { category, options, isAutomated } = req;

  const [loadingSkillName, setLoadingSkillName] = useState(null);
  const [isCreateDomainSkillModalOpen, setIsCreateDomainSkillModalOpen] = useState(false);
  const [selectedCategoryForSkill, setSelectedCategoryForSkill] = useState(null);

  const closeSkillModal = useCallback(() => {
    setIsCreateDomainSkillModalOpen(false);
    setSelectedCategoryForSkill(null);
  }, []);

  const openSkillModal = useCallback((category) => {
    setSelectedCategoryForSkill(category);
    setIsCreateDomainSkillModalOpen(true);
  }, []);

  const handleCreateSkill = (domainSkillData) => {
    logger.log("ModifyOptions:handleCreateDomainSkill", "create domain skill", {
      domainSkillData
    });

    const ref = Actions.createDomainSkill.completed.listen(() => {
      setIsCreateDomainSkillModalOpen(false);
      setSelectedCategoryForSkill(null);

      if (profile?.jobCategoryId) {
        Actions.getDomainSkillsByJobCategoryId(profile.jobCategoryId);
      }

      const update = updateProfileArrays({
        profile,
        index,
        updatedRequirement: req,
        filteredSkills
      });

      logger.log(
        "ModifyOptions:handleCreateDomainSkill",
        "update chatbot questions with new domain skill",
        {
          profileId: profile.id,
          update,
          newSkill: domainSkillData.name
        }
      );
      Actions.patchProfile(profile.id, update);
    });

    Actions.createDomainSkill(domainSkillData);

    return () => {
      ref();
    };
  };

  useEffect(() => {
    const ref = Actions.patchProfile.completed.listen(() => {
      setLoadingSkillName(null);
    });

    const failedRef = Actions.patchProfile.failed.listen(() => {
      setLoadingSkillName(null);
    });

    return () => {
      ref();
      failedRef();
    };
  }, []);

  const handleToggleOption = (skillName) => {
    setLoadingSkillName(skillName);

    const newUpdate = {
      requirements: [...profile.requirements],
      chatbotQuestions: [...profile.chatbotQuestions]
    };

    const updatedOptions = toggleListOption(skillName, req.options);
    newUpdate.requirements[index] = { ...req, options: updatedOptions };

    if (isAutomated) {
      newUpdate.chatbotQuestions = newUpdate.chatbotQuestions.map((question) =>
        question.name === req.category
          ? mapRequirementToChatbotQuestion({ ...req, options: updatedOptions }, filteredSkills)
          : question
      );
    }

    logger.log("ModifyOptions:handleToggleOption", "toggle on/off the category option", {
      profileId: profile.id,
      newUpdate
    });
    Actions.patchProfile(profile.id, newUpdate);
  };

  const isEmpty = filteredSkills.length === 0;

  return (
    <>
      {isEmpty ? (
        <NoSkillsText>{t("No skills available for this category")}</NoSkillsText>
      ) : (
        <OptionsList>
          {filteredSkills.map((name) => {
            const isSelected = options.some((option) => option.name === name);

            return (
              <div key={name} style={{ position: "relative" }}>
                <OptionBtn
                  $isActive={isSelected}
                  $isLoading={loadingSkillName === name}
                  onClick={() => handleToggleOption(name)}
                >
                  <span className="text-content">{name}</span>
                  <LoadingIndicator className="loading-content" color={loadingColor} />
                </OptionBtn>
              </div>
            );
          })}
        </OptionsList>
      )}
      <ActionButton
        icon={<PlusCircle size="20px" />}
        variant="link"
        text={filteredSkills.length === 0 ? t("Add one") : t("Add another")}
        onClick={() => openSkillModal(category)}
        styles={CreateDomainStyles}
      />

      <Modal isOpen={isCreateDomainSkillModalOpen} setIsOpen={closeSkillModal}>
        <CreateDomainSkillForm
          onSubmit={handleCreateSkill}
          onCancel={closeSkillModal}
          jobCategoryId={profile.jobCategoryId}
          categoryId={req.categoryId}
          category={selectedCategoryForSkill}
        />
      </Modal>
    </>
  );
};

export default withRouter(withRefluxStore(ListOption, [ProfilesStore], ["profile", "userProfile"]));
